import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { ArrowLeft, Loader2, Upload, X } from 'lucide-react'
import imageCompression from 'browser-image-compression'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { COLOR_PRESETS } from '@/lib/constants'
import {
  usePaymentMethod,
  usePaymentMethods,
  useCreatePaymentMethod,
  useUpdatePaymentMethod,
  useDeletePaymentMethod,
  useUploadPaymentMethodImage,
  useDeletePaymentMethodImage,
  usePaymentMethodImageUrls,
} from '@/lib/hooks'
import type { PaymentMethod } from '@/lib/hooks'
import { paymentMethodSchema, PAYMENT_METHOD_BRANDS } from '@/lib/schemas'
import type { PaymentMethodBrand, PaymentMethodType } from '@/lib/schemas'
import type { PaymentMethodInsert } from '@/lib/api'
import type { Scope } from '@/lib/db-types'
import { PaymentMethodCard } from '@/components/payment-method/payment-method-card'

const LINKED_BANK_NONE = '__none__'

type FormState = {
  type: PaymentMethodType
  scope: Scope
  name: string
  color: string
  brand: PaymentMethodBrand
  last4: string
  closingDay: string // '1'..'31' or 'eom'
  paymentDay: string
  paymentMonthOffset: '1' | '2'
  bankName: string
  branchName: string
  linkedBankMethodId: string // '' = 未設定
}

const initialForm: FormState = {
  type: 'credit_card',
  scope: 'household',
  name: '',
  color: COLOR_PRESETS[0],
  brand: 'VISA',
  last4: '',
  closingDay: '27',
  paymentDay: '10',
  paymentMonthOffset: '1',
  bankName: '',
  branchName: '',
  linkedBankMethodId: '',
}

function formStateFromSource(s: PaymentMethod): FormState {
  return {
    type: s.type,
    scope: s.scope,
    name: s.name,
    color: s.color ?? COLOR_PRESETS[0],
    brand: (s.brand ?? 'VISA') as PaymentMethodBrand,
    last4: s.last4 ?? '',
    closingDay: s.closing_day_end_of_month ? 'eom' : (s.closing_day ? String(s.closing_day) : '27'),
    paymentDay: s.payment_day_end_of_month ? 'eom' : (s.payment_day ? String(s.payment_day) : '10'),
    paymentMonthOffset: (s.payment_month_offset === 2 ? '2' : '1'),
    bankName: s.bank_name ?? '',
    branchName: s.branch_name ?? '',
    linkedBankMethodId: s.linked_bank_method_id ?? '',
  }
}

function validateForm(form: FormState): boolean {
  const base = {
    name: form.name.trim(),
    scope: form.scope,
    color: form.color || undefined,
    is_active: true,
  }

  const candidate = form.type === 'credit_card'
    ? {
        ...base,
        type: 'credit_card' as const,
        brand: form.brand,
        last4: form.last4,
        closing: form.closingDay === 'eom'
          ? { endOfMonth: true as const }
          : { day: Number(form.closingDay) },
        payment: form.paymentDay === 'eom'
          ? { endOfMonth: true as const }
          : { day: Number(form.paymentDay) },
        paymentMonthOffset: form.paymentMonthOffset === '2' ? 2 as const : 1 as const,
      }
    : {
        ...base,
        type: 'bank_account' as const,
        bankName: form.bankName || null,
        branchName: form.branchName || null,
      }

  const result = paymentMethodSchema.safeParse(candidate)
  if (!result.success) {
    toast.error(result.error.issues[0].message)
    return false
  }
  return true
}

function buildPayload(form: FormState): Partial<PaymentMethodInsert> {
  const base = {
    type: form.type,
    scope: form.scope,
    name: form.name.trim(),
    color: form.color || null,
    is_active: true,
  }

  if (form.type === 'credit_card') {
    const closingEom = form.closingDay === 'eom'
    const paymentEom = form.paymentDay === 'eom'
    const closingDayNum = closingEom ? null : Number(form.closingDay)
    const paymentDayNum = paymentEom ? null : Number(form.paymentDay)
    return {
      ...base,
      brand: form.brand,
      last4: form.last4,
      closing_day: closingDayNum,
      closing_day_end_of_month: closingEom,
      payment_day: paymentDayNum,
      payment_day_end_of_month: paymentEom,
      payment_month_offset: form.paymentMonthOffset === '2' ? 2 : 1,
      bank_name: null,
      branch_name: null,
      linked_bank_method_id: form.linkedBankMethodId || null,
      image_path: null,
    }
  }

  return {
    ...base,
    brand: null,
    last4: null,
    closing_day: null,
    closing_day_end_of_month: false,
    payment_day: null,
    payment_day_end_of_month: false,
    payment_month_offset: 1,
    bank_name: form.bankName || null,
    branch_name: form.branchName || null,
    linked_bank_method_id: null,
    image_path: null,
  }
}

export default function PaymentMethodEditPage() {
  const { id } = useParams<{ id?: string }>()
  const navigate = useNavigate()
  const isEdit = !!id

  const { data: editingItem, isLoading: loadingItem } = usePaymentMethod(id)
  const { data: allPaymentMethods = [] } = usePaymentMethods()
  const createPs = useCreatePaymentMethod()
  const updatePs = useUpdatePaymentMethod()
  const deletePs = useDeletePaymentMethod()
  const uploadImage = useUploadPaymentMethodImage()
  const deleteImage = useDeletePaymentMethodImage()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [form, setForm] = useState<FormState>(initialForm)
  const [uploading, setUploading] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    if (!isEdit) {
      if (!initialized) {
        setForm(initialForm)
        setInitialized(true)
      }
      return
    }
    if (editingItem && !initialized) {
      setForm(formStateFromSource(editingItem))
      setInitialized(true)
    }
  }, [isEdit, editingItem, initialized])

  const imageList = editingItem ? [editingItem] : []
  const { data: imageUrlMap = {} } = usePaymentMethodImageUrls(imageList)
  const currentImageUrl = editingItem?.image_path ? imageUrlMap[editingItem.image_path] : null

  const handleSave = () => {
    if (!validateForm(form)) return
    const payload = buildPayload(form)

    if (isEdit && editingItem) {
      updatePs.mutate(
        { id: editingItem.id, data: payload },
        {
          onSuccess: () => {
            navigate(`/settings/payment-methods/${editingItem.id}`, { replace: true })
          },
        },
      )
    } else {
      createPs.mutate(payload as PaymentMethodInsert, {
        onSuccess: () => {
          navigate('/settings/payment-methods', { replace: true })
        },
      })
    }
  }

  const handleSelectFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!editingItem) {
      toast.error('保存後に画像をアップロードできます')
      return
    }
    setUploading(true)
    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 1200,
      })
      await uploadImage.mutateAsync({ id: editingItem.id, file: compressed as File })
      toast.success('画像をアップロードしました')
    } catch (err) {
      console.error(err)
    } finally {
      setUploading(false)
    }
  }

  const handleRemoveImage = () => {
    if (!editingItem) return
    deleteImage.mutate(editingItem.id)
  }

  const handleDelete = () => {
    if (!editingItem) return
    deletePs.mutate(editingItem.id, {
      onSuccess: () => {
        setDeleteOpen(false)
        navigate('/settings/payment-methods', { replace: true })
      },
    })
  }

  if (isEdit && loadingItem && !editingItem) {
    return (
      <div className="mx-auto max-w-lg px-4 py-6">
        <Skeleton className="h-44 w-full rounded-xl" />
      </div>
    )
  }

  if (isEdit && !editingItem && !loadingItem) {
    return (
      <div className="mx-auto max-w-lg px-4 py-6">
        <p className="text-sm text-muted-foreground">支払い方法が見つかりません</p>
      </div>
    )
  }

  const isPending = createPs.isPending || updatePs.isPending
  const backTo = isEdit && editingItem
    ? `/settings/payment-methods/${editingItem.id}`
    : '/settings/payment-methods'

  const previewSource: PaymentMethod = isEdit && editingItem
    ? { ...editingItem, ...buildPayload(form) } as PaymentMethod
    : ({
        id: 'preview',
        type: form.type,
        scope: form.scope,
        name: form.name || '新しい支払い方法',
        color: form.color,
        image_path: null,
        brand: form.type === 'credit_card' ? form.brand : null,
        last4: form.type === 'credit_card' ? form.last4 : null,
        closing_day: null,
        closing_day_end_of_month: false,
        payment_day: null,
        payment_day_end_of_month: false,
        payment_month_offset: 1,
        bank_name: form.type === 'bank_account' ? form.bankName : null,
        branch_name: null,
        linked_bank_method_id: form.type === 'credit_card' ? (form.linkedBankMethodId || null) : null,
        sort_order: 0,
        is_active: true,
        created_by: '',
        created_at: '',
        updated_at: '',
      } satisfies PaymentMethod)

  return (
    <div className="mx-auto max-w-lg px-4 py-6 pb-32">
      <div className="mb-4 flex items-center gap-2">
        <Link to={backTo} replace>
          <Button variant="ghost" size="icon">
            <ArrowLeft className="size-5" />
          </Button>
        </Link>
        <h1 className="text-xl font-semibold">
          {isEdit ? '支払い方法を編集' : '支払い方法を追加'}
        </h1>
      </div>

      <div className="flex justify-center py-2">
        <PaymentMethodCard
          source={previewSource}
          imageUrl={currentImageUrl ?? undefined}
          size="lg"
        />
      </div>

      <div className="space-y-4 pt-4">
        <div className="space-y-2">
          <Label>種別</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={form.type === 'credit_card' ? 'default' : 'outline'}
              size="sm"
              className="flex-1"
              onClick={() => setForm((p) => ({ ...p, type: 'credit_card' }))}
              disabled={isEdit}
            >
              クレジットカード
            </Button>
            <Button
              type="button"
              variant={form.type === 'bank_account' ? 'default' : 'outline'}
              size="sm"
              className="flex-1"
              onClick={() => setForm((p) => ({ ...p, type: 'bank_account' }))}
              disabled={isEdit}
            >
              銀行口座
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <Label>区分</Label>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={form.scope === 'household' ? 'default' : 'outline'}
              size="sm"
              className="flex-1"
              onClick={() => setForm((p) => ({ ...p, scope: 'household' }))}
            >
              家計（家族で共有）
            </Button>
            <Button
              type="button"
              variant={form.scope === 'personal' ? 'default' : 'outline'}
              size="sm"
              className="flex-1"
              onClick={() => setForm((p) => ({ ...p, scope: 'personal' }))}
            >
              個人
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="ps-name">名前</Label>
          <Input
            id="ps-name"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            placeholder={form.type === 'credit_card' ? '例: メインJCB' : '例: 住信SBIネット銀行'}
            autoCapitalize="off"
          />
        </div>

        <div className="space-y-2">
          <Label>色</Label>
          <div className="flex flex-wrap gap-2">
            {COLOR_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                className={`size-8 rounded-full border-2 transition-transform hover:scale-110 ${
                  form.color === preset ? 'border-foreground' : 'border-transparent'
                }`}
                style={{ backgroundColor: preset }}
                onClick={() => setForm((p) => ({ ...p, color: preset }))}
                aria-label={`色: ${preset}`}
              />
            ))}
          </div>
        </div>

        {form.type === 'credit_card' && (
          <>
            <div className="space-y-2">
              <Label>ブランド</Label>
              <div className="flex gap-2">
                {PAYMENT_METHOD_BRANDS.map((brand) => (
                  <Button
                    key={brand}
                    type="button"
                    variant={form.brand === brand ? 'default' : 'outline'}
                    size="sm"
                    className="flex-1"
                    onClick={() => setForm((p) => ({ ...p, brand }))}
                  >
                    {brand}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ps-last4">下4桁</Label>
              <Input
                id="ps-last4"
                inputMode="numeric"
                value={form.last4}
                onChange={(e) =>
                  setForm((p) => ({ ...p, last4: e.target.value.replace(/\D/g, '').slice(-4) }))
                }
                placeholder="1234"
              />
            </div>

            <div className="space-y-2">
              <Label>締め日</Label>
              <Select
                value={form.closingDay}
                onValueChange={(v) => setForm((p) => ({ ...p, closingDay: v }))}
              >
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="eom">末日</SelectItem>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <SelectItem key={d} value={String(d)}>{d}日</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>引き落とし日</Label>
              {/* 月オフセットは補助情報として小さいトグルで上に。 */}
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant={form.paymentMonthOffset === '1' ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 flex-1 text-xs"
                  onClick={() => setForm((p) => ({ ...p, paymentMonthOffset: '1' }))}
                >
                  翌月
                </Button>
                <Button
                  type="button"
                  variant={form.paymentMonthOffset === '2' ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 flex-1 text-xs"
                  onClick={() => setForm((p) => ({ ...p, paymentMonthOffset: '2' }))}
                >
                  翌々月
                </Button>
              </div>
              <Select
                value={form.paymentDay}
                onValueChange={(v) => setForm((p) => ({ ...p, paymentDay: v }))}
              >
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="eom">末日</SelectItem>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <SelectItem key={d} value={String(d)}>{d}日</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>引き落とし口座</Label>
              {(() => {
                // RLS で見える銀行口座のうち、 現在のカード scope と互換のあるものに絞る。
                // (DB トリガーが最終防衛線だが、 UX 上は事前に弾く)
                const availableBanks = allPaymentMethods.filter((m) => {
                  if (m.type !== 'bank_account') return false
                  if (!m.is_active && m.id !== form.linkedBankMethodId) return false
                  if (form.scope === 'household') return m.scope === 'household'
                  // personal card: 自分の personal、 または household
                  return m.scope === 'household' || m.created_by === editingItem?.created_by
                })
                if (availableBanks.length === 0) {
                  return (
                    <p className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                      銀行口座を先に登録すると、このカードの引き落とし口座として紐付けられます。
                    </p>
                  )
                }
                return (
                  <Select
                    value={form.linkedBankMethodId || LINKED_BANK_NONE}
                    onValueChange={(v) =>
                      setForm((p) => ({
                        ...p,
                        linkedBankMethodId: v === LINKED_BANK_NONE ? '' : v,
                      }))
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="未設定" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={LINKED_BANK_NONE}>未設定</SelectItem>
                      {availableBanks.map((b) => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.name}
                          {b.bank_name && ` (${b.bank_name})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )
              })()}
            </div>
          </>
        )}

        {form.type === 'bank_account' && (
          <>
            <div className="space-y-2">
              <Label htmlFor="ps-bank-name">銀行名</Label>
              <Input
                id="ps-bank-name"
                value={form.bankName}
                onChange={(e) => setForm((p) => ({ ...p, bankName: e.target.value }))}
                placeholder="例: 三菱UFJ銀行"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ps-branch-name">支店名</Label>
              <Input
                id="ps-branch-name"
                value={form.branchName}
                onChange={(e) => setForm((p) => ({ ...p, branchName: e.target.value }))}
                placeholder="例: 渋谷支店"
              />
            </div>
          </>
        )}

        {isEdit && editingItem && (
          <div className="space-y-2">
            <Label>券面画像</Label>
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleSelectFile}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Upload className="size-4" />
                )}
                画像を選択
              </Button>
              {editingItem.image_path && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveImage}
                  disabled={deleteImage.isPending}
                >
                  <X className="size-4" />
                  削除
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              画像はプライバシー保護のため、表示時のみ署名付きURLで読み込まれます。
            </p>
          </div>
        )}

        {!isEdit && (
          <p className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            画像のアップロードは保存後に行えます。
          </p>
        )}
      </div>

      <div className="space-y-2 pt-6">
        <Button
          className="w-full"
          onClick={handleSave}
          disabled={isPending || !form.name.trim() || (form.type === 'credit_card' && form.last4.length !== 4)}
        >
          {isPending ? '保存中...' : '保存'}
        </Button>
        {isEdit && (
          <Button
            variant="destructive"
            className="w-full"
            onClick={() => setDeleteOpen(true)}
          >
            削除
          </Button>
        )}
      </div>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>支払い方法を削除しますか？</DialogTitle>
            <DialogDescription>
              削除すると、紐付いている固定支出の支払い方法は未設定になります（固定支出自体は削除されません）。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              キャンセル
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deletePs.isPending}
            >
              {deletePs.isPending && <Loader2 className="size-4 animate-spin" />}
              削除する
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
